// Email service for sending alerts
const BASE = process.env.REACT_APP_API_URL || 'https://0g75gzq525.execute-api.us-east-1.amazonaws.com';
const N8N_WEBHOOK = 'https://n8n.digitransolutions.in/webhook/twinmaker-send-alert';

export interface EmailAlert {
  to: string;
  subject: string;
  severity: 'CRITICAL' | 'WARNING' | 'INFO';
  entity: string;
  message: string;
  machineId?: string;
  sensorId?: string;
  timestamp: string;
  twinId: string;
}

export interface NotificationPreferences {
  email: string;
  emailOnCritical: boolean;
  emailOnWarning: boolean;
  emailOnInfo: boolean;
  additionalRecipients: string[];
  createdAt?: string;
  updatedAt?: string;
}

export const emailApi = {
  // Get user notification preferences
  getPreferences: async (): Promise<NotificationPreferences | null> => {
    try {
      const res = await fetch(`${BASE}/notifications/preferences`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('cf_token')}`,
        },
      });

      if (!res.ok) {
        console.error('[emailApi] Failed to fetch preferences:', res.status);
        return null;
      }

      return await res.json();
    } catch (error) {
      console.error('[emailApi] Preference fetch failed:', error);
      return null;
    }
  },

  // Update user notification preferences
  updatePreferences: async (prefs: Partial<NotificationPreferences>): Promise<boolean> => {
    try {
      const res = await fetch(`${BASE}/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('cf_token')}`,
        },
        body: JSON.stringify(prefs),
      });

      if (!res.ok) {
        const error = await res.json();
        console.error('[emailApi] Preference update failed:', error);
        return false;
      }

      console.log('[emailApi] Preferences updated successfully');
      return true;
    } catch (error) {
      console.error('[emailApi] Preference update error:', error);
      return false;
    }
  },

  // Send alert email to user — disabled for background service alerts
  // Only What-If simulation alerts go to n8n (via simulationStore)
  sendAlert: async (_alert: EmailAlert) => {
    return null;
  },

  // Send batch alerts
  sendBatchAlerts: async (alerts: EmailAlert[]) => {
    try {
      const res = await fetch(`${BASE}/notifications/send-batch-alerts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('cf_token')}`,
        },
        body: JSON.stringify({ alerts }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to send batch alerts');
      }

      return await res.json();
    } catch (error) {
      console.error('Batch email send failed:', error);
      return null;
    }
  },

  // Send daily digest
  sendDailyDigest: async (twinId: string, userEmail: string) => {
    try {
      const res = await fetch(`${BASE}/notifications/send-daily-digest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('cf_token')}`,
        },
        body: JSON.stringify({ twinId, userEmail }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to send daily digest');
      }

      return await res.json();
    } catch (error) {
      console.error('Daily digest send failed:', error);
      return null;
    }
  },
};
